<script setup>
import { computed, watch, onUnmounted, nextTick, getCurrentInstance } from 'vue';
import FormFieldsets from './FormFieldsets';

const props = defineProps({
    formId: {
        type: String,
        required: true
    },
    showLegends: Boolean,
    hideHelpText: Boolean,
    showOptions: Boolean
});

const { proxy } = getCurrentInstance();
const store = proxy.$store;

// --- Computed ---

const formState = computed(() => store.state[props.formId]);

const formShow = computed(() => formState.value?.show);

const currentData = computed(() => formState.value?.currentData);

const legends = computed(() => formState.value?.legends);

const id = computed(() => formState.value?.id || 0);

const clickedAnchor = computed(() => formState.value?.clickedAnchor);

const top = computed(() => {
    const firstLegend = legends.value?.[0];
    const fieldset = getFieldset(firstLegend);
    return fieldset ? fieldset.getBoundingClientRect().top : 0;
});

const scrollableEl = computed(() => {
    return proxy.$refs.form?.closest('.sidebar-toggle-section');
});

// --- Methods ---

const getFieldset = (legend) => {
    if (legend && proxy.$refs.form) {
        const idSafeLegend = legend.replace(/[^A-Z0-9]+/ig, '_');
        return proxy.$refs.form.querySelector(`fieldset#${idSafeLegend}`);
    }
    return null;
};

const scrollTop = () => {
    if (scrollableEl.value) {
        scrollableEl.value.scroll(0, 0);
    }
};

const scrollToLegend = (legend) => {
    const fieldset = getFieldset(legend);
    if (!fieldset) return;

    // The form must be scrolled top before we obtain the fieldset's top position below.
    scrollTop();

    nextTick(() => {
        const targetTop = fieldset.getBoundingClientRect().top - top.value;
        scrollableEl.value.scroll(0, targetTop);
    });
};

const save = () => {
    store.dispatch(`${props.formId}/save`);
};

// --- Watchers ---

watch(() => props.formId, () => {
    nextTick(() => {
        if (proxy.$refs.form) {
            store.commit(`${props.formId}/setFormEl`, proxy.$refs.form);
        }
        // Always scroll to the top when showing new forms.
        scrollTop();
    });
}, { immediate: true });

watch(clickedAnchor, (newValue) => {
    if (newValue) {
        scrollToLegend(newValue);
    }
});

// --- Lifecycle ---

onUnmounted(() => {
    store.unregisterModule(props.formId);
});
</script>

<template>
    <form v-if="currentData" ref="form" autocomplete="off" novalidate @submit.prevent="save">
        <FormFieldsets
            v-if="formShow"
            :form-id="formId"
            :show-legends="showLegends"
            :hide-help-text="hideHelpText"
        />

        <Dump>{{
                {
                    isModified: store.getters[formId + '/isModified'](),
                    modifiedData: store.getters[formId + '/modifiedData'](),
                    currentData: store.state[formId].currentData,
                    originalData: store.state[formId].originalData
                }
            }}
        </Dump>
    </form>
</template>